import { useState } from 'react';
import { Button, Divider, EditableTitle } from 'src/components/atoms';
import { StyledIcon } from 'src/components/foundations/foundations.styles';
import {
  Accordion,
  GroupParameter,
  InformationBanner,
  Modal,
  handleSuccessToast,
} from 'src/components/molecules';
import { DefaultOption } from 'src/components/molecules/DropdownSelect';
import { ParameterType } from 'src/components/molecules/Parameter/helpers';
import { GroupType } from 'src/graphql';
import { FormState } from 'src/types';
import { useTheme } from 'styled-components';
import { saveGroupCsv } from 'src/utils/groups/csvDownload';
import { DateFormats, formatDate } from 'src/utils/time';
import {
  DividerContainer,
  EmptyGroupBannerContainer,
  GroupAuthor,
  GroupContainer,
  GroupDate,
  GroupFooter,
  GroupFooterRight,
  GroupParameterList,
  GroupSubtitle,
  GroupTitle,
} from './Group.styles';
import { GroupMembers } from './GroupMembers';
import { groupMessages } from './groupMessages';
import { useGroup } from './useGroup';

export type GroupAttributes = {
  department: DefaultOption[];
  country: DefaultOption[];
  site: DefaultOption[];
  team: DefaultOption[];
};

interface Props {
  group: GroupType;
  attributes: GroupAttributes;
  index: number;
  groupsLength: number;
}

export const Group = ({ group, attributes, index, groupsLength }: Props) => {
  const theme = useTheme();
  const [name, setName] = useState(group.name ?? '');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const {
    groupUserCount,
    isGroupUserCountLoading,
    updateGroup,
    deleteGroup,
    parameterList,
    setParameterList,
    parameterOptions,
  } = useGroup(group);

  return (
    <>
      <Accordion
        title={
          <GroupTitle>
            <EditableTitle
              value={name}
              setValue={(value: string) => setName(value)}
              defaultValue="Untitled Group Name"
              data-testid={`group-name-${group.id}`}
            />
          </GroupTitle>
        }
        subtitle={
          <GroupSubtitle>
            <StyledIcon size="24px" className="uil uil-users-alt" />
            <GroupDate>
              {groupUserCount?.groupUserCount.count ?? 0}{' '}
              {groupUserCount?.groupUserCount.count === 1
                ? 'member'
                : 'members'}
            </GroupDate>
            <Divider
              variant="vertical"
              height="20px"
              color={
                theme.isVaultVariant
                  ? theme.newPalette.neutral.N400
                  : theme.atlas.core.color.gray[400]
              }
            />
            <GroupDate>
              Created {formatDate(group.createdAt, DateFormats.DD_MMM_YYYY)}
            </GroupDate>
            <Divider
              variant="vertical"
              height="20px"
              color={
                theme.isVaultVariant
                  ? theme.newPalette.neutral.N400
                  : theme.atlas.core.color.gray[400]
              }
            />
            <GroupAuthor>
              By {(group.CreatedBy?.displayName || group.CreatedBy?.name) ?? ''}
            </GroupAuthor>
          </GroupSubtitle>
        }
        last={true}
        topBorder
        shadow
        topBorderRadius="12px"
        bottomBorderRadius="12px"
        defaultOpen={index === groupsLength - 1}
        data-testid="group-accordion"
      >
        <GroupContainer>
          {groupUserCount?.groupUserCount.count === 0 && (
            <EmptyGroupBannerContainer>
              <InformationBanner
                state={FormState.WARNING}
                title={groupMessages.emptyGroupWarningTitle}
                description={groupMessages.emptyGroupWarningDescription}
                $fullWidth
                data-testid="empty-group-warning-banner"
              />
            </EmptyGroupBannerContainer>
          )}
          <GroupParameterList>
            {Object.keys(parameterList).map((parameter, index) => (
              <GroupParameter
                key={parameter}
                parameter={parameter as ParameterType}
                isFirstParameter={index === 0}
                parameterOptions={parameterOptions}
                parameterList={parameterList}
                setParameterList={setParameterList}
                attributes={attributes}
                data-testid={group.id}
              />
            ))}
          </GroupParameterList>
          <GroupMembers
            members={groupUserCount?.groupUserCount?.items ?? []}
            groupId={group.id}
            isLoading={isGroupUserCountLoading}
          />
          <DividerContainer>
            <Divider
              variant="horizontal"
              color={
                theme.isVaultVariant
                  ? theme.newPalette.neutral.N300
                  : theme.atlas.core.color.gray[200]
              }
            />
          </DividerContainer>
          <GroupFooter>
            <Button
              size="medium"
              variant="secondary"
              width="140px"
              onClick={() => setDeleteModalOpen(true)}
            >
              Delete group
            </Button>
            <GroupFooterRight>
              <Button
                size="medium"
                variant="secondary"
                width="140px"
                onClick={() =>
                  saveGroupCsv(
                    groupUserCount?.groupUserCount.items ?? [],
                    group.name ?? 'Untitled Group',
                  )
                }
                disabled={groupUserCount?.groupUserCount.count === 0}
              >
                Download CSV
              </Button>
              <Button
                size="medium"
                variant="primary"
                width="140px"
                onClick={() => {
                  const attributes = { ...parameterList };
                  if (attributes['']) {
                    delete attributes[''];
                  }
                  updateGroup({
                    parameters: { id: group.id, name, attributes },
                  });

                  handleSuccessToast({
                    toastMessage: 'Group saved successfully!',
                  });
                }}
                data-testid={`save-group-changes-button-${group.id}`}
              >
                Save changes
              </Button>
            </GroupFooterRight>
          </GroupFooter>
        </GroupContainer>
      </Accordion>
      <Modal
        size="medium"
        title="Are you sure you want to delete this Group?"
        description={
          <>
            <span>
              Deleting this Group will permanently remove it from the system.
              Any associated configurations or dependencies may be affected.
            </span>
            <br></br>
            <br></br>
            <span>
              To confirm deletion, press &quot;Delete Group&quot; below.
            </span>
          </>
        }
        open={deleteModalOpen}
        setOpen={setDeleteModalOpen}
        onCancel={() => setDeleteModalOpen(false)}
        onConfirm={() => {
          setDeleteModalOpen(false);
          deleteGroup({ id: group.id });
        }}
        confirmButtonText="Delete Group"
      />
    </>
  );
};
